import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    Button as MuiButton,
    MenuItem as MuiMenuItem,
    Select,
    styled,
    Tab,
    Tabs,
    TextField,
    Theme,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useContext, useEffect, useState } from "react";
import { ChromePicker } from "react-color";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";

import { Enum_Border_Style } from "src/api/category/enum";
import {
    mutationCreateElement,
    mutationUpdateElement,
    useGetElements,
} from "src/api/element";
import { useGetFormByType } from "src/api/form";
import File from "src/components/File";
import Input from "src/components/Input";
import { FormContext } from "src/contexts/form";
import { LanguageContext } from "src/contexts/language";
import { MapContext } from "src/contexts/map";
import MuiDateTimePicker from "src/genericMuiInputs/DateTimePicker";
import MuiTextField from "src/genericMuiInputs/TextField";
import { Enum_Action_Type, Enum_Geometry_Types } from "src/types";
import { Type_Category } from "src/types/category";
import {
    Type_Element,
    Type_Element_Data,
    Type_Elements_By_Category,
} from "src/types/element";
import { Type_Form, Type_Input_Item } from "src/types/form";
import { getIconFromPicto, sortedCategories } from "src/utils/category";
import { addElementSchema } from "src/validators/addElement";


const StyledForm = styled("form")({
    width: "85%",
    display: "flex",
    flexDirection: "column",
});

const MenuItem = styled(MuiMenuItem)<{ isparent: string }>(
    ({ theme, isparent }) => ({
        paddingLeft: theme.spacing(JSON.parse(isparent) ? 1 : 5),
    }),
);

const AddElement = () => {
    const { openForm, handleForm, type, actionType } = useContext(FormContext);
    const { selectedMap, categories, newElement, connectedUser } =
        useContext(MapContext);
    const { language } = useContext(LanguageContext);
    const { t } = useTranslation();

    const { data, isLoading } = useGetElements(selectedMap?.id);
    const { data: dynamicForm, refetch } = useGetFormByType(type!);
    const queryClient = useQueryClient();
    const { mutateAsync: mutateUpdateElement } = mutationUpdateElement() || {};
    const { mutateAsync: mutateCreateElement } = mutationCreateElement() || {};

    useEffect(() => {
        if (type) refetch();
    }, [type]);

    const methods = useForm({
        resolver: yupResolver(addElementSchema(t)),
        defaultValues: {
            title: newElement?.title ?? "",
            description: newElement?.description ?? "",
            category_id: newElement?.category_id ?? undefined,
            start_date: newElement?.start_date ?? dayjs(),
            end_date: newElement?.end_date ?? null,
            parent_id: newElement?.parent_id ?? null,
            border_width: newElement?.style?.border_width || null,
            border_style: newElement?.style?.border_style || null,
            primary_color: newElement?.style?.primary_color,
            background_color: newElement?.style?.background_color,
        },
    });

    const [selectedTab, setSelectedTab] = useState(0);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setSelectedTab(newValue);
    };

    const {
        handleSubmit,
        control,
        formState: { errors },
        watch,
    } = methods;

    const isMobile = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down("md"),
    );

    const onSubmit = async (data: Partial<Type_Element_Data>) => {
        const defaultDataStyle = {
            border_width: null,
            border_style: null,
            primary_color: undefined,
            background_color: undefined,
        };

        const filteredStyle = Object.fromEntries(
            Object.entries({
                border_width: data.border_width,
                border_style: data.border_style,
                primary_color: data.primary_color,
                background_color: data.background_color,
            }).filter(
                ([key, value]) =>
                    value !==
                    defaultDataStyle[key as keyof typeof defaultDataStyle],
            ),
        );

        const elementObj: Partial<Type_Element> = {
            title: data.title!,
            description: data.description!,
            category_id: data.category_id!,
            start_date: data.start_date!,
            data: newElement?.data,
            end_date: !data.end_date ? data.start_date : data.end_date,
            coordinates: newElement!.coordinates,
            files: newElement!.files,
            parent_id: data.parent_id,
            style:
                Object.keys(filteredStyle).length > 0
                    ? filteredStyle
                    : undefined,
            user_id: connectedUser?.id,
            map_id: selectedMap!.id,
        };

        if (newElement?.data !== null) elementObj["data"] = newElement?.data;

        if (elementObj.parent_id === null) delete elementObj.parent_id;

        if (actionType === Enum_Action_Type.create) {
            await mutateCreateElement(elementObj);
        } else {
            if (newElement?.id) {
                await mutateUpdateElement({
                    elementId: newElement.id,
                    body: elementObj,
                });
            }
        }
        queryClient.invalidateQueries(["elements", selectedMap?.id]);
        handleForm(false);
    };

    const theme = useTheme();

    const categoryId = watch("category_id");

    return (
        <Dialog
            open={openForm}
            onClose={() => handleForm(false)}
            fullWidth
            maxWidth="md"
            sx={{
                "& .MuiDialog-paper": {
                    [theme.breakpoints.down("md")]: {
                        position: "absolute",
                        bottom: 0,
                        margin: 0,
                        width: "100%",
                    },
                },
            }}
        >
            <FormProvider {...methods}>
                <Box display="flex" justifyContent="center">
                    <StyledForm
                        onSubmit={handleSubmit(
                            (data) =>
                                void onSubmit(
                                    data as unknown as Type_Element_Data,
                                ),
                        )}
                    >
                        <DialogTitle>
                            <Box position="relative">
                                <IconButton
                                    onClick={() => handleForm(false)}
                                    size="small"
                                    edge="end"
                                    sx={{
                                        position: "absolute",
                                        top: 0,
                                        right: 0,
                                    }}
                                >
                                    {!isMobile && (
                                        <FontAwesomeIcon icon={faTimes} />
                                    )}
                                </IconButton>
                            </Box>
                        </DialogTitle>

                        <DialogContent>
                            <Tabs
                                value={selectedTab}
                                onChange={handleTabChange}
                                centered={isMobile ? true : false}
                            >
                                <Tab
                                    label={
                                        <strong>
                                            {t("addElement.information_tab")}
                                        </strong>
                                    }
                                />
                                <Tab
                                    label={
                                        <strong>
                                            {t("addElement.appearences_tab")}
                                        </strong>
                                    }
                                />
                            </Tabs>

                            <Box mt={2}>
                                {selectedTab === 0 && (
                                    <Grid
                                        height={theme.spacing(80)}
                                        container
                                        direction="row"
                                        spacing={2}
                                    >
                                        <Grid item xs={isMobile ? 12 : 6}>
                                            <Typography color="text.secondary">
                                                {t("addElement.event")}
                                            </Typography>
                                            <MuiTextField
                                                fullWidth
                                                name="title"
                                                placeholder={t(
                                                    "addElement.eventPlaceholder",
                                                )}
                                                error={!!errors.title}
                                                helperText={
                                                    errors?.title?.message
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={isMobile ? 12 : 6}>
                                            <Typography color="text.secondary">
                                                {t("addElement.category")}
                                            </Typography>
                                            <Controller
                                                name="category_id"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControl
                                                        fullWidth
                                                        error={
                                                            !!errors.category_id
                                                        }
                                                    >
                                                        <Select
                                                            fullWidth
                                                            {...field}
                                                            value={
                                                                field.value ||
                                                                ""
                                                            }
                                                        >
                                                            {Object.values(
                                                                sortedCategories(
                                                                    categories.filter(
                                                                        (
                                                                            item: Type_Category,
                                                                        ) =>
                                                                            item.nature ===
                                                                                type ||
                                                                            item.nature ===
                                                                                null,
                                                                    ),
                                                                    language,
                                                                ),
                                                            ).map((item) => (
                                                                <MenuItem
                                                                    disabled={
                                                                        item.hasChildren &&
                                                                        item.parentId ===
                                                                            null
                                                                    }
                                                                    key={
                                                                        item.id
                                                                    }
                                                                    value={
                                                                        item.id
                                                                    }
                                                                    isparent={(
                                                                        item.parentId ===
                                                                        null
                                                                    ).toString()}
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={getIconFromPicto(
                                                                            item.picto,
                                                                        )}
                                                                        color={
                                                                            item
                                                                                .style
                                                                                .primary_color
                                                                        }
                                                                    />
                                                                    <Box
                                                                        component="span"
                                                                        ml={1}
                                                                    >
                                                                        {item
                                                                            .title[
                                                                            language
                                                                        ] || ""}
                                                                    </Box>
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                        {errors.category_id && (
                                                            <FormHelperText>
                                                                {
                                                                    errors
                                                                        .category_id
                                                                        .message
                                                                }
                                                            </FormHelperText>
                                                        )}
                                                    </FormControl>
                                                )}
                                            />
                                        </Grid>

                                        <Grid item xs={isMobile ? 12 : 6}>
                                            <Typography color="text.secondary">
                                                {t("addElement.startDate")}
                                            </Typography>
                                            <MuiDateTimePicker
                                                rules={{
                                                    required: t(
                                                        "addElement.validator.required",
                                                    ),
                                                }}
                                                name="start_date"
                                            />
                                        </Grid>

                                        <Grid item xs={isMobile ? 12 : 6}>
                                            <Typography color="text.secondary">
                                                {t("addElement.endDate")}
                                            </Typography>
                                            <MuiDateTimePicker name="end_date" />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Typography color="text.secondary">
                                                {t("addElement.description")}
                                            </Typography>
                                            <TextField
                                                {...methods.register(
                                                    "description",
                                                )}
                                                name="description"
                                                placeholder={t(
                                                    "addElement.placeholder",
                                                )}
                                                fullWidth
                                                multiline
                                                rows={4}
                                                variant="outlined"
                                                error={!!errors?.description}
                                                helperText={
                                                    errors?.description?.message
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            {dynamicForm &&
                                                dynamicForm.map(
                                                    (
                                                        form: Type_Form,
                                                        index: number,
                                                    ) => (
                                                        <Box
                                                            display="flex"
                                                            flexDirection="column"
                                                            gap={1}
                                                            key={index}
                                                        >
                                                            {Array.isArray(
                                                                form.inputs,
                                                            ) &&
                                                                form.inputs.map(
                                                                    (
                                                                        input: Type_Input_Item,
                                                                        inputIndex: number,
                                                                    ) => (
                                                                        <Input
                                                                            key={
                                                                                inputIndex
                                                                            }
                                                                            data={
                                                                                input
                                                                            }
                                                                        />
                                                                    ),
                                                                )}
                                                        </Box>
                                                    ),
                                                )}
                                        </Grid>
                                        <Grid item xs={12}>
                                            <File />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography color="text.secondary">
                                                {t("addElement.parent")}
                                            </Typography>
                                            <Controller
                                                name="parent_id"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        fullWidth
                                                        {...field}
                                                        value={
                                                            field.value || ""
                                                        }
                                                    >
                                                        {!isLoading &&
                                                            data.map(
                                                                (
                                                                    category: Type_Elements_By_Category,
                                                                ) => [
                                                                    <MuiMenuItem
                                                                        key={
                                                                            category.category_id
                                                                        }
                                                                        disabled
                                                                    >
                                                                        {
                                                                            category
                                                                                .title[
                                                                                language
                                                                            ]
                                                                        }
                                                                    </MuiMenuItem>,

                                                                    ...category.elements.map(
                                                                        (
                                                                            element: Omit<
                                                                                Type_Element_Data,
                                                                                "category_id"
                                                                            >,
                                                                        ) => (
                                                                            <MenuItem
                                                                                isparent="false"
                                                                                key={
                                                                                    element.id
                                                                                }
                                                                                value={
                                                                                    element.id
                                                                                }
                                                                            >
                                                                                {
                                                                                    element.title
                                                                                }
                                                                            </MenuItem>
                                                                        ),
                                                                    ),
                                                                ],
                                                            )}
                                                    </Select>
                                                )}
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                            </Box>

                            <Box mt={2}>
                                {selectedTab === 1 && (
                                    <Grid
                                        container
                                        height={theme.spacing(80)}
                                        spacing={2}
                                        display="flex"
                                        direction="row"
                                        alignItems="flex-start"
                                        justifyContent="flex-start"
                                    >
                                        {type !== Enum_Geometry_Types.POINT && (
                                            <>
                                                <Grid
                                                    item
                                                    xs={isMobile ? 12 : 6}
                                                >
                                                    <Typography>
                                                        {t(
                                                            "category.border_width",
                                                        )}
                                                    </Typography>
                                                    <Controller
                                                        name="border_width"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                fullWidth
                                                                size="small"
                                                                type="number"
                                                                error={
                                                                    !!errors?.border_width
                                                                }
                                                                helperText={
                                                                    errors
                                                                        ?.border_width
                                                                        ?.message
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid
                                                    item
                                                    xs={isMobile ? 12 : 6}
                                                >
                                                    <Typography>
                                                        {t(
                                                            "category.border_style",
                                                        )}
                                                    </Typography>
                                                    <Controller
                                                        name="border_style"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <FormControl
                                                                fullWidth
                                                                size="small"
                                                            >
                                                                <Select
                                                                    {...field}
                                                                >
                                                                    {Object.values(
                                                                        Enum_Border_Style,
                                                                    ).map(
                                                                        (
                                                                            value,
                                                                        ) => (
                                                                            <MuiMenuItem
                                                                                key={
                                                                                    value
                                                                                }
                                                                                value={
                                                                                    value
                                                                                }
                                                                            >
                                                                                {t(
                                                                                    `border_style.${value}`,
                                                                                )}
                                                                            </MuiMenuItem>
                                                                        ),
                                                                    )}
                                                                </Select>
                                                            </FormControl>
                                                        )}
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                        <Grid
                                            item
                                            xs={isMobile ? 12 : 6}
                                            mt={0}
                                        >
                                            <Typography>
                                                {t("category.primary_color")}
                                            </Typography>
                                            <Controller
                                                name="primary_color"
                                                control={control}
                                                defaultValue={
                                                    categories.find(
                                                        (cat) =>
                                                            cat.id ===
                                                            categoryId,
                                                    )?.style.primary_color
                                                }
                                                render={({ field }) => (
                                                    <ChromePicker
                                                        color={
                                                            field.value as string
                                                        }
                                                        onChangeComplete={(
                                                            color,
                                                        ) =>
                                                            field.onChange(
                                                                color.hex,
                                                            )
                                                        }
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        {type ===
                                            Enum_Geometry_Types.POLYGON && (
                                            <Grid item xs={isMobile ? 12 : 6}>
                                                <Typography>
                                                    {t(
                                                        "category.background_color",
                                                    )}
                                                </Typography>
                                                <Controller
                                                    name="background_color"
                                                    control={control}
                                                    defaultValue={
                                                        categories.find(
                                                            (cat) =>
                                                                cat.id ===
                                                                categoryId,
                                                        )?.style
                                                            .background_color
                                                    }
                                                    render={({ field }) => (
                                                        <ChromePicker
                                                            color={
                                                                field.value as string
                                                            }
                                                            onChangeComplete={(
                                                                color,
                                                            ) =>
                                                                field.onChange(
                                                                    color.hex,
                                                                )
                                                            }
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        )}
                                        <Grid item xs={12} height="30%"></Grid>
                                    </Grid>
                                )}
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <MuiButton
                                variant="contained"
                                type="submit"
                                sx={{ m: 2 }}
                            >
                                {t("addElement.save")}
                            </MuiButton>
                        </DialogActions>
                    </StyledForm>
                </Box>
            </FormProvider>
        </Dialog>
    );
};

export default AddElement;
